import { useEffect, useRef } from 'react';
import type { KeyboardEvent, ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui';
import { BILLING_REGIONS, useBillingRegionLabels } from '@/lib/billing-region';
import { cx } from '@/lib/cx';
import { formatDateTime } from '@/lib/format';
import { useEnquirySummary } from '@/lib/use-enquiry-summary';
import type { EnquiryAlert, TargetBillingRegion } from '@/lib/types';

/**
 * The enquiry alert stack: a card per new event-interest submission, B2B
 * proposal or report lead.
 *
 * Portalled to the body on the overlay rung (z-50), BOTTOM-RIGHT from `sm`
 * and bottom-centre below it, so it never collides with the task-reminder
 * stack, which owns top centre. Like that stack it is not a modal:
 * `pointer-events-none` on the container, `pointer-events-auto` on each card.
 */

const MAX_VISIBLE = 3;

/** Same key the task reminders store their chime preference under ('1' / '0'). */
const CHIME_KEY = 'emerge.reminders.chime';

type EnquirySource = EnquiryAlert['source'];

const EYEBROW: Record<EnquirySource, string> = {
  'event-interest': 'Event interest submission',
  'b2b-proposal': 'B2B proposal',
  'report-download': 'Report lead',
};

/** The `?tab=` key on `/event-interest` each source lives under. */
const TAB: Record<EnquirySource, string> = {
  'event-interest': 'submissions',
  'b2b-proposal': 'proposals',
  'report-download': 'reports',
};

/* Region names are admin-editable on the billing-region master, so they are
 * FETCHED (useBillingRegionLabels) and never written out here. The hook runs
 * inside AlertCard, so the master is read only while a card is on screen. */
const KNOWN_REGIONS: readonly string[] = BILLING_REGIONS;

function hrefFor(alert: EnquiryAlert): string {
  return `/event-interest?tab=${TAB[alert.source]}`;
}

function keyOf(alert: EnquiryAlert): string {
  return `${alert.source}:${alert.id}`;
}

function secondaryLine(alert: EnquiryAlert): string | null {
  const parts = [alert.contactName, alert.detail].filter(
    (part): part is string => part !== null && part.trim() !== '',
  );
  return parts.length > 0 ? parts.join(' · ') : null;
}

/* ------------------------------------------------------------------ *
 * Relative time.
 * ------------------------------------------------------------------ */

function relativeSince(iso: string): string | null {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  const seconds = Math.round((then - Date.now()) / 1000);
  let formatter: Intl.RelativeTimeFormat;
  try {
    formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  } catch {
    return null;
  }
  const abs = Math.abs(seconds);
  if (abs < 60) return formatter.format(0, 'second');
  if (abs < 3600) return formatter.format(Math.round(seconds / 60), 'minute');
  if (abs < 86_400) return formatter.format(Math.round(seconds / 3600), 'hour');
  return formatter.format(Math.round(seconds / 86_400), 'day');
}

/* ------------------------------------------------------------------ *
 * Chime — a tiny Web Audio two-tone, created lazily, errors swallowed.
 * ------------------------------------------------------------------ */

type AudioContextCtor = typeof AudioContext;

let sharedAudio: AudioContext | null = null;

function chimeMuted(): boolean {
  try {
    return localStorage.getItem(CHIME_KEY) === '0';
  } catch {
    return false;
  }
}

function playChime(): void {
  if (typeof window === 'undefined' || chimeMuted()) return;
  try {
    if (!sharedAudio) {
      const scoped = window as unknown as { webkitAudioContext?: AudioContextCtor };
      const Ctor = window.AudioContext ?? scoped.webkitAudioContext;
      if (!Ctor) return;
      sharedAudio = new Ctor();
    }
    const ctx = sharedAudio;
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
    const start = ctx.currentTime + 0.02;
    const notes: [number, number][] = [
      [1046.5, 0],
      [1318.5, 0.14],
    ];
    for (const [frequency, offset] of notes) {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      const at = start + offset;
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.05, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.16);
      oscillator.connect(gain).connect(ctx.destination);
      oscillator.start(at);
      oscillator.stop(at + 0.18);
    }
  } catch {
    // The card is the alert; the sound is a nicety.
  }
}

/* ------------------------------------------------------------------ *
 * Icons.
 * ------------------------------------------------------------------ */

const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

function CloseIcon() {
  return (
    <svg {...iconProps} className="h-4 w-4 shrink-0">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

function InboxIcon() {
  return (
    <svg {...iconProps} className="h-6 w-6 shrink-0">
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}

const LINK_BUTTON =
  'inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-md border px-3 text-sm font-medium transition-colors duration-150 ease-out motion-reduce:transition-none max-sm:h-11';

/* ------------------------------------------------------------------ *
 * One card.
 * ------------------------------------------------------------------ */

function AlertCard({
  alert,
  onDismiss,
}: {
  alert: EnquiryAlert;
  onDismiss: (alert: EnquiryAlert) => void;
}) {
  const eyebrow = EYEBROW[alert.source];
  const secondary = secondaryLine(alert);
  const regionLabel = useBillingRegionLabels();
  const region =
    alert.billingRegion && KNOWN_REGIONS.includes(alert.billingRegion)
      ? regionLabel(alert.billingRegion as TargetBillingRegion)
      : null;
  const when = alert.submittedAt ? relativeSince(alert.submittedAt) : null;

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    onDismiss(alert);
  }

  return (
    <div
      role="group"
      aria-label={`${eyebrow}: ${alert.title}`}
      onKeyDown={onKeyDown}
      className="pointer-events-auto overflow-hidden rounded-lg border border-border bg-surface-raised shadow-lg"
    >
      <div className="flex items-start gap-3 px-4 pt-4">
        <span className="mt-0.5 text-fg-muted">
          <InboxIcon />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-fg-muted">
            {eyebrow}
          </p>
          <p className="mt-1 text-base leading-snug font-semibold text-fg [overflow-wrap:anywhere]">
            {alert.title}
          </p>
          {secondary && (
            <p className="mt-1 text-sm leading-relaxed text-fg-secondary [overflow-wrap:anywhere]">
              {secondary}
            </p>
          )}
          {(region || when) && (
            <p className="mt-1 flex flex-wrap gap-x-2 text-xs text-fg-muted">
              {region && <span>{region}</span>}
              {when && alert.submittedAt && (
                <time dateTime={alert.submittedAt} title={formatDateTime(alert.submittedAt)}>
                  {when}
                </time>
              )}
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          iconOnly
          aria-label={`Dismiss ${alert.title}`}
          onClick={() => onDismiss(alert)}
          leadingIcon={<CloseIcon />}
        />
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2 px-4 pt-3 pb-4">
        <Button variant="ghost" onClick={() => onDismiss(alert)}>
          Dismiss
        </Button>
        <Link
          to={hrefFor(alert)}
          onClick={() => onDismiss(alert)}
          className={cx(
            LINK_BUTTON,
            'border-transparent bg-accent-brand text-fg-on-accent hover:bg-accent-brand-hover active:bg-accent-brand-strong',
          )}
        >
          Open
        </Link>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The component.
 * ------------------------------------------------------------------ */

export function EnquiryAlerts(): ReactElement | null {
  const {
    alerts,
    dismissAlert,
  }: { alerts: EnquiryAlert[]; dismissAlert: (alert: EnquiryAlert) => void } =
    useEnquirySummary();
  const navigate = useNavigate();
  const seenRef = useRef<Set<string>>(new Set());

  /*
   * A key present now and absent last render is NEW: one chime for the batch,
   * one desktop notification per new alert — and only if permission was
   * already granted. This component never asks for it.
   */
  useEffect(() => {
    const current = new Set(alerts.map(keyOf));
    const fresh = alerts.filter((alert) => !seenRef.current.has(keyOf(alert)));
    seenRef.current = current;
    if (fresh.length === 0) return;

    playChime();

    try {
      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
      for (const alert of fresh.slice(0, MAX_VISIBLE)) {
        try {
          const notification = new Notification(alert.title, {
            body: secondaryLine(alert) ?? EYEBROW[alert.source],
            tag: `enquiry-${alert.source}-${alert.id}`,
          });
          notification.onclick = () => {
            try {
              window.focus();
            } catch {
              /* focus is best-effort */
            }
            navigate(hrefFor(alert));
            notification.close();
          };
        } catch {
          // The constructor throws on some platforms; the card still shows.
        }
      }
    } catch {
      /* Notification API unavailable */
    }
  }, [alerts, navigate]);

  // Newest first, at most three on screen.
  const visible = [...alerts]
    .sort((a, b) => {
      const at = a.submittedAt ? Date.parse(a.submittedAt) : 0;
      const bt = b.submittedAt ? Date.parse(b.submittedAt) : 0;
      return (Number.isNaN(bt) ? 0 : bt) - (Number.isNaN(at) ? 0 : at);
    })
    .slice(0, MAX_VISIBLE);
  const newest = visible[0] ?? null;

  return (
    <>
      {/* Always mounted, so the first message is announced. */}
      <p role="status" aria-live="polite" className="sr-only">
        {newest ? `${EYEBROW[newest.source]}: ${newest.title}` : ''}
      </p>
      {visible.length > 0 &&
        createPortal(
          <div
            role="region"
            aria-label="Enquiry alerts"
            className="pointer-events-none fixed bottom-4 left-1/2 z-50 flex w-[min(24rem,calc(100vw-1.5rem))] -translate-x-1/2 flex-col gap-2 sm:right-4 sm:left-auto sm:translate-x-0"
          >
            {visible.map((alert) => (
              <AlertCard key={keyOf(alert)} alert={alert} onDismiss={dismissAlert} />
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
